import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterSidebar = ({ isOpen, onClose, onApplyFilters, activeFilters }) => {
  const [filters, setFilters] = useState({
    dateRange: { start: '', end: '' },
    carriers: [],
    statuses: [],
    users: [],
    savedPreset: ''
  });

  const carrierOptions = [
    { id: 'ups', label: 'UPS', count: 1247 },
    { id: 'fedex', label: 'FedEx', count: 892 },
    { id: 'usps', label: 'USPS', count: 2156 },
    { id: 'dhl', label: 'DHL', count: 334 }
  ];

  const statusOptions = [
    { id: 'delivered', label: 'Delivered', count: 3421, color: 'success' },
    { id: 'in-transit', label: 'In Transit', count: 892, color: 'warning' },
    { id: 'pending', label: 'Pending', count: 156, color: 'muted' },
    { id: 'exception', label: 'Exception', count: 67, color: 'error' }
  ];

  const userOptions = [
    { id: 'sarah-chen', label: 'Sarah Chen' },
    { id: 'mike-johnson', label: 'Mike Johnson' },
    { id: 'emily-davis', label: 'Emily Davis' },
    { id: 'james-wilson', label: 'James Wilson' }
  ];

  const savedPresets = [
    { id: 'today', label: 'Today\'s Shipments', icon: 'Calendar' },
    { id: 'week', label: 'Last 7 Days', icon: 'Clock' },
    { id: 'month', label: 'This Month', icon: 'CalendarDays' },
    { id: 'exceptions', label: 'All Exceptions', icon: 'AlertTriangle' },
    { id: 'pending', label: 'Pending Validation', icon: 'Clock' }
  ];

  const handleCarrierToggle = (carrierId) => {
    setFilters(prev => ({
      ...prev,
      carriers: prev?.carriers?.includes(carrierId)
        ? prev?.carriers?.filter(id => id !== carrierId)
        : [...prev?.carriers, carrierId]
    }));
  };

  const handleStatusToggle = (statusId) => {
    setFilters(prev => ({
      ...prev,
      statuses: prev?.statuses?.includes(statusId)
        ? prev?.statuses?.filter(id => id !== statusId)
        : [...prev?.statuses, statusId]
    }));
  };

  const handleUserToggle = (userId) => {
    setFilters(prev => ({
      ...prev,
      users: prev?.users?.includes(userId)
        ? prev?.users?.filter(id => id !== userId)
        : [...prev?.users, userId]
    }));
  };

  const handlePresetClick = (presetId) => {
    setFilters(prev => ({ ...prev, savedPreset: presetId }));
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleReset = () => {
    setFilters({
      dateRange: { start: '', end: '' },
      carriers: [],
      statuses: [],
      users: [],
      savedPreset: ''
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.dateRange?.start || filters?.dateRange?.end) count++;
    count += filters?.carriers?.length;
    count += filters?.statuses?.length;
    count += filters?.users?.length;
    if (filters?.savedPreset) count++;
    return count;
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-150 lg:hidden"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`
          fixed top-[60px] right-0 bottom-0 w-full sm:w-[400px] lg:w-[360px]
          bg-card border-l border-border shadow-elevation-4 z-200
          overflow-y-auto
          animate-in slide-in-from-right duration-200
        `}
        role="complementary"
        aria-label="Filter sidebar"
      >
        <div className="sticky top-0 bg-card border-b border-border z-10">
          <div className="flex items-center justify-between px-24 py-18">
            <div className="flex items-center gap-12">
              <h2 className="font-heading font-semibold text-h5 text-foreground">
                Filters
              </h2>
              {getActiveFilterCount() > 0 && (
                <div className="px-8 py-2 bg-primary/10 rounded-full">
                  <span className="font-caption font-medium text-[12px] text-primary">
                    {getActiveFilterCount()}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="w-[36px] h-[36px] flex items-center justify-center rounded-lg hover:bg-muted transition-smooth"
              aria-label="Close filters"
            >
              <Icon name="X" size={20} color="var(--color-foreground)" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="p-24 space-y-32">
          <div>
            <h3 className="font-body font-semibold text-[14px] text-foreground mb-12">
              Saved Presets
            </h3>
            <div className="space-y-6">
              {savedPresets?.map(preset => (
                <button
                  key={preset?.id}
                  onClick={() => handlePresetClick(preset?.id)}
                  className={`
                    w-full flex items-center gap-12 px-18 py-12 rounded-lg
                    transition-smooth text-left
                    ${filters?.savedPreset === preset?.id
                      ? 'bg-primary/10 text-primary' :'hover:bg-muted text-foreground'
                    }
                  `}
                >
                  <Icon 
                    name={preset?.icon} 
                    size={18} 
                    color="currentColor" 
                    strokeWidth={2} 
                  />
                  <span className="font-body text-[14px]">{preset?.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-border pt-24">
            <h3 className="font-body font-semibold text-[14px] text-foreground mb-12">
              Date Range
            </h3>
            <div className="space-y-12">
              <Input
                type="date"
                label="Start Date"
                value={filters?.dateRange?.start}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  dateRange: { ...prev?.dateRange, start: e?.target?.value }
                }))}
              />
              <Input
                type="date"
                label="End Date"
                value={filters?.dateRange?.end}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  dateRange: { ...prev?.dateRange, end: e?.target?.value }
                }))}
              />
            </div>
          </div>

          <div className="border-t border-border pt-24">
            <h3 className="font-body font-semibold text-[14px] text-foreground mb-12">
              Carriers
            </h3>
            <div className="space-y-12">
              {carrierOptions?.map(carrier => (
                <Checkbox
                  key={carrier?.id}
                  label={
                    <div className="flex items-center justify-between w-full">
                      <span>{carrier?.label}</span>
                      <span className="font-caption text-[12px] text-muted-foreground">
                        {carrier?.count?.toLocaleString()}
                      </span>
                    </div>
                  }
                  checked={filters?.carriers?.includes(carrier?.id)}
                  onChange={() => handleCarrierToggle(carrier?.id)}
                />
              ))}
            </div>
          </div>

          <div className="border-t border-border pt-24">
            <h3 className="font-body font-semibold text-[14px] text-foreground mb-12">
              Status
            </h3>
            <div className="space-y-12">
              {statusOptions?.map(status => (
                <Checkbox
                  key={status?.id}
                  label={
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-8">
                        <div className={`w-[8px] h-[8px] rounded-full bg-${status?.color}`} />
                        <span>{status?.label}</span>
                      </div>
                      <span className="font-caption text-[12px] text-muted-foreground">
                        {status?.count?.toLocaleString()}
                      </span>
                    </div>
                  }
                  checked={filters?.statuses?.includes(status?.id)}
                  onChange={() => handleStatusToggle(status?.id)}
                />
              ))}
            </div>
          </div>

          <div className="border-t border-border pt-24">
            <h3 className="font-body font-semibold text-[14px] text-foreground mb-12">
              Entered By
            </h3>
            <div className="space-y-12">
              {userOptions?.map(user => (
                <Checkbox
                  key={user?.id}
                  label={user?.label}
                  checked={filters?.users?.includes(user?.id)}
                  onChange={() => handleUserToggle(user?.id)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-24">
          <div className="flex gap-12">
            <Button
              variant="outline"
              onClick={handleReset}
              fullWidth
            >
              Reset
            </Button>
            <Button
              variant="default"
              onClick={handleApply}
              fullWidth
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default FilterSidebar;